/**
 * Maintenance Dashboard
 * Jobs, Quotes, Contractors & Invoices management
 */
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Wrench, FileText, HardHat, Receipt, Calendar, Search,
  Plus, Clock, CheckCircle, AlertCircle, DollarSign, Phone, Mail,
  Building2, Star, Shield, Edit, Trash2, Loader2, Send, User,
  AlertTriangle, CheckCircle2, XCircle, RefreshCw, Copy, MapPin
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { API } from '@/App';
import { toast } from 'sonner';

export default function MaintenanceDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'jobs');
  const [stats, setStats] = useState({
    open_jobs: 0,
    in_progress: 0,
    pending_quotes: 0,
    completed_this_month: 0,
    unpaid_invoices: 0,
    total_contractors: 0,
    open_issues: 0,
  });
  const [jobs, setJobs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Contractors state
  const [contractors, setContractors] = useState([]);
  const [loadingContractors, setLoadingContractors] = useState(false);
  const [showAddContractorModal, setShowAddContractorModal] = useState(false);
  const [editingContractor, setEditingContractor] = useState(null);
  const [savingContractor, setSavingContractor] = useState(false);
  const [contractorForm, setContractorForm] = useState({
    contact_name: '',
    company_name: '',
    email: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    postcode: '',
    categories: [],
    services: [],
    hourly_rate: '',
    insurance_expiry: '',
    insurance_verified: false,
    notes: ''
  });
  const [contractorFilter, setContractorFilter] = useState('all');
  
  // New Job Modal state
  const [showNewJobModal, setShowNewJobModal] = useState(false);
  const [savingJob, setSavingJob] = useState(false);
  const [properties, setProperties] = useState([]);
  const [jobForm, setJobForm] = useState({
    property_id: '',
    property_address: '',
    issue_type: '',
    description: '',
    priority: 'medium',
    scheduled_date: '',
    access_instructions: '',
    contractor_id: '',
    issue_id: ''
  });
  
  // Issues Inbox state
  const [issues, setIssues] = useState([]);
  const [loadingIssues, setLoadingIssues] = useState(false);
  
  // Contractor detail view state
  const [viewingContractor, setViewingContractor] = useState(null);
  
  // When creating a new job, show all active contractors (override smart filter)
  const [showAllContractorsInJobForm, setShowAllContractorsInJobForm] = useState(false);
  
  // Custom trade input (Add Contractor modal)
  const [customTradeInput, setCustomTradeInput] = useState('');
  
  const token = localStorage.getItem('token');
  
  const categoryOptions = [
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'plumber', label: 'Plumber' },
    { value: 'electrician', label: 'Electrician' },
    { value: 'gas_engineer', label: 'Gas Engineer' },
    { value: 'handyman', label: 'Handyman' },
    { value: 'cleaner', label: 'Cleaner' },
    { value: 'locksmith', label: 'Locksmith' },
    { value: 'roofer', label: 'Roofer' },
    { value: 'builder', label: 'Builder' },
    { value: 'painter', label: 'Painter/Decorator' },
    { value: 'gardener', label: 'Gardener' },
    { value: 'carpet_fitter', label: 'Carpet Fitter' },
    { value: 'appliance_repair', label: 'Appliance Repair' },
    { value: 'pest_control', label: 'Pest Control' },
    { value: 'drainage', label: 'Drainage' },
    { value: 'epc_assessor', label: 'EPC Assessor' },
    { value: 'inventory_clerk', label: 'Inventory Clerk' }
  ];
  
  useEffect(() => {
    fetchStats();
    fetchJobs();
    fetchProperties();
    // Contractors are needed by the New Job modal's "Assign Contractor" dropdown
    fetchContractors();
  }, []);
  
  useEffect(() => {
    setSearchParams({ tab: activeTab });
    if (activeTab === 'contractors') {
      fetchContractors();
    }
    if (activeTab === 'issues') {
      fetchIssues();
    }
  }, [activeTab]);
  
  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/lettings/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(response.data?.properties || []);
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  };
  
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/maintenance/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };
  
  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/jobs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // API returns jobs as direct array or wrapped in { jobs: [] }
      const jobsData = Array.isArray(response.data) ? response.data : (response.data.jobs || []);
      setJobs(jobsData);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchContractors = async () => {
    try {
      setLoadingContractors(true);
      const response = await axios.get(`${API}/contractors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const contractorsData = response.data?.contractors || response.data || [];
      setContractors(contractorsData);
    } catch (error) {
      console.error('Error fetching contractors:', error);
      toast.error('Failed to load contractors');
    } finally {
      setLoadingContractors(false);
    }
  };
  
  const fetchIssues = async () => {
    try {
      setLoadingIssues(true);
      const response = await axios.get(`${API}/issues`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const issuesData = Array.isArray(response.data) ? response.data : (response.data?.issues || []);
      setIssues(issuesData);
    } catch (error) {
      console.error('Error fetching issues:', error);
      toast.error('Failed to load tenant issues');
    } finally {
      setLoadingIssues(false);
    }
  };
  
  const handleQuickJobFromIssue = (issue) => {
    // Map issue severity / emergency flag to job priority
    let priority = 'medium';
    if (issue.is_emergency) priority = 'emergency';
    else if (issue.severity === 'high' || issue.severity === 'emergency') priority = 'high';
    else if (issue.severity === 'low') priority = 'low';
    
    // Normalize the issue_type from the tenant input to one of our SelectItem values
    const issueTypeMap = {
      'electrical': 'electrical',
      'plumbing': 'plumbing',
      'heating': 'heating',
      'boiler': 'heating',
      'gas': 'gas',
      'appliance': 'appliance',
      'appliances': 'appliance',
      'lock': 'lock',
      'locks': 'lock',
      'keys': 'lock',
      'pest': 'pest',
      'pests': 'pest',
      'damp': 'damp',
      'mould': 'damp',
      'mold': 'damp',
      'roof': 'roof',
      'roofing': 'roof',
      'window': 'window',
      'windows': 'window',
      'door': 'window',
      'doors': 'window',
      'garden': 'garden',
      'external': 'garden',
      'cleaning': 'cleaning',
      'general': 'general',
      'repair': 'general',
      'inspection': 'inspection',
      'other': 'other'
    };
    const rawType = (issue.issue_type || '').toLowerCase().trim();
    const normalizedType = issueTypeMap[rawType] || 'other';
    
    setJobForm({
      property_id: issue.property_id || '',
      property_address: issue.property_address || issue.property_postcode || '',
      issue_type: normalizedType,
      description: issue.description || '',
      priority,
      scheduled_date: '',
      access_instructions: '',
      contractor_id: '',
      issue_id: issue.issue_id
    });
    setShowNewJobModal(true);
  };
  
  const handleCreateJob = async () => {
    if (!jobForm.issue_type || !jobForm.description) {
      toast.error('Issue type and description are required');
      return;
    }
    setSavingJob(true);
    try {
      await axios.post(`${API}/jobs`, jobForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Job created successfully');
      setShowNewJobModal(false);
      setShowAllContractorsInJobForm(false);
      setJobForm({
        property_id: '',
        property_address: '',
        issue_type: '',
        description: '',
        priority: 'medium',
        scheduled_date: '',
        access_instructions: '',
        contractor_id: '',
        issue_id: ''
      });
      fetchJobs();
      fetchStats();
      // Refresh issues inbox if it was opened from there
      if (activeTab === 'issues') fetchIssues();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create job');
    } finally {
      setSavingJob(false);
    }
  };
  
  const handleSaveContractor = async () => {
    if (!contractorForm.contact_name || !contractorForm.phone) {
      toast.error('Contact name and phone are required');
      return;
    }
    setSavingContractor(true);
    try {
      const payload = {
        ...contractorForm,
        hourly_rate: contractorForm.hourly_rate ? parseFloat(contractorForm.hourly_rate) : null
      };
      
      if (editingContractor) {
        await axios.put(`${API}/contractors/${editingContractor.contractor_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Contractor updated');
      } else {
        await axios.post(`${API}/contractors`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Contractor added');
      }
      
      setShowAddContractorModal(false);
      setEditingContractor(null);
      resetContractorForm();
      fetchContractors();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save contractor');
    } finally {
      setSavingContractor(false);
    }
  };
  
  const handleDeleteContractor = async (contractorId) => {
    if (!confirm('Are you sure you want to delete this contractor?')) return;
    try {
      await axios.delete(`${API}/contractors/${contractorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Contractor deleted');
      fetchContractors();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete contractor');
    }
  };
  
  // Result dialog shown after sending/resending portal invite — contains the
  // generated temp password so admins can share it manually while emails are off.
  const [inviteResult, setInviteResult] = useState(null); // { contractor, resent, email, temp_password }
  
  const handleSendInvite = async (contractor) => {
    if (!contractor.email) {
      toast.error('Contractor has no email address');
      return;
    }
    const wasInvited = !!contractor.portal_invited;
    if (wasInvited) {
      const ok = window.confirm(
        `Resend portal access to ${contractor.email}?\n\n` +
        `This will generate a NEW temporary password and invalidate the contractor's existing password.`
      );
      if (!ok) return;
    }
    try {
      const res = await axios.post(
        `${API}/contractors/${contractor.contractor_id}/invite`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.message || `Portal invite sent to ${contractor.email}`);
      setInviteResult({
        contractor,
        resent: !!res.data?.resent,
        email: res.data?.email || contractor.email,
        temp_password: res.data?.temp_password,
        portal_url: res.data?.portal_url,
      });
      fetchContractors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    }
  };
  
  const resetContractorForm = () => {
    setContractorForm({
      contact_name: '',
      company_name: '',
      email: '',
      phone: '',
      address_line1: '',
      address_line2: '',
      city: '',
      postcode: '',
      categories: [],
      services: [],
      hourly_rate: '',
      insurance_expiry: '',
      insurance_verified: false,
      notes: ''
    });
    setCustomTradeInput('');
  };
  
  const openEditContractor = (contractor) => {
    setEditingContractor(contractor);
    setContractorForm({
      // Fall back to legacy field names so older contractors still populate the form
      contact_name: contractor.contact_name || contractor.name || '',
      company_name: contractor.company_name || contractor.company || '',
      email: contractor.email || '',
      phone: contractor.phone || '',
      address_line1: contractor.address_line1 || '',
      address_line2: contractor.address_line2 || '',
      city: contractor.city || '',
      postcode: contractor.postcode || '',
      categories: contractor.categories || (contractor.specialty ? [contractor.specialty] : []),
      services: contractor.services || [],
      hourly_rate: contractor.hourly_rate?.toString() || '',
      insurance_expiry: contractor.insurance_expiry || '',
      insurance_verified: contractor.insurance_verified || false,
      notes: contractor.notes || ''
    });
    setShowAddContractorModal(true);
  };
  
  const toggleCategory = (category) => {
    setContractorForm(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
  };
  
  // Slugify a free-text trade name into the same lowercase_underscore format
  // we use for built-in categories (e.g. "Tree Surgeon" -> "tree_surgeon")
  const slugifyTrade = (raw) =>
    (raw || '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  
  // Human-readable label for any trade value (built-in or custom slug)
  const tradeLabel = (value) => {
    const std = categoryOptions.find(o => o.value === value);
    if (std) return std.label;
    return value
      .split('_')
      .filter(Boolean)
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  };
  
  const addCustomTrade = () => {
    const slug = slugifyTrade(customTradeInput);
    if (!slug) {
      toast.error('Enter a trade name');
      return;
    }
    if (contractorForm.categories.includes(slug)) {
      toast.error(`"${tradeLabel(slug)}" is already added`);
      return;
    }
    setContractorForm(prev => ({
      ...prev,
      categories: [...prev.categories, slug]
    }));
    setCustomTradeInput('');
  };
  
  const getInsuranceStatus = (contractor) => {
    if (!contractor.insurance_expiry) return 'unknown';
    const expiry = new Date(contractor.insurance_expiry);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    if (daysUntilExpiry < 0) return 'expired';
    if (daysUntilExpiry < 30) return 'expiring';
    return 'valid';
  };
  
  const filteredContractors = contractors.filter(c => {
    const matchesSearch = !searchTerm || 
      c.contact_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = contractorFilter === 'all' || 
      (contractorFilter === 'active' && c.status === 'active') ||
      (contractorFilter === 'inactive' && c.status !== 'active') ||
      c.categories?.includes(contractorFilter) ||
      c.specialty === contractorFilter;
    
    return matchesSearch && matchesFilter;
  });
  
  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return <Badge className={styles[status] || 'bg-slate-100 text-slate-800'}>{status}</Badge>;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-slate-900">Maintenance</h1>
          <p className="text-slate-500">Manage jobs, quotes, contractors and invoices</p>
        </div>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('jobs')}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Wrench className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.open_jobs}</p>
                  <p className="text-xs text-slate-500">Open Jobs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('jobs')}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Clock className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.in_progress}</p>
                  <p className="text-xs text-slate-500">In Progress</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('quotes')}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.pending_quotes}</p>
                  <p className="text-xs text-slate-500">Pending Quotes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('jobs')}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.completed_this_month}</p>
                  <p className="text-xs text-slate-500">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('invoices')}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Receipt className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.unpaid_invoices}</p>
                  <p className="text-xs text-slate-500">Unpaid Invoices</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('contractors')}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-100 rounded-lg">
                  <HardHat className="h-5 w-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_contractors}</p>
                  <p className="text-xs text-slate-500">Contractors</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex items-center justify-between mb-4">
            <TabsList>
              <TabsTrigger value="jobs" className="gap-2" data-testid="tab-jobs">
                <Wrench className="h-4 w-4" />
                Jobs
              </TabsTrigger>
              <TabsTrigger value="issues" className="gap-2 relative" data-testid="tab-issues">
                <AlertTriangle className="h-4 w-4" />
                Issues Inbox
                {stats.open_issues > 0 && (
                  <Badge
                    className="ml-1 h-5 min-w-[20px] px-1.5 bg-red-500 hover:bg-red-500 text-white text-[10px] font-semibold border-0"
                    data-testid="issues-inbox-badge"
                  >
                    {stats.open_issues > 99 ? '99+' : stats.open_issues}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="quotes" className="gap-2">
                <FileText className="h-4 w-4" />
                Quotes
              </TabsTrigger>
              <TabsTrigger value="contractors" className="gap-2">
                <HardHat className="h-4 w-4" />
                Contractors
              </TabsTrigger>
              <TabsTrigger value="invoices" className="gap-2">
                <Receipt className="h-4 w-4" />
                Invoices
              </TabsTrigger>
            </TabsList>
            
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input 
                  placeholder="Search..." 
                  className="pl-9 w-64"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <Button onClick={() => setShowNewJobModal(true)}>
                <Plus className="h-4 w-4 mr-2" />
                New Job
              </Button>
            </div>
          </div>
          
          <TabsContent value="jobs">
            <Card>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-8 text-center text-slate-500">Loading jobs...</div>
                ) : jobs.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">No jobs found</div>
                ) : (
                  <div className="divide-y">
                    {jobs.slice(0, 20).map(job => (
                      <div 
                        key={job.job_id} 
                        className="p-4 hover:bg-slate-50 cursor-pointer flex items-center justify-between"
                        onClick={() => navigate(`/maintenance/job/${job.job_id}`)}
                      >
                        <div>
                          <p className="font-medium text-slate-900">{job.title || 'Maintenance Job'}</p>
                          <p className="text-sm text-slate-500">{job.property_address || 'No address'}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          {getStatusBadge(job.status)}
                          <span className="text-sm text-slate-400">
                            {new Date(job.created_at).toLocaleDateString('en-GB')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="issues">
            <Card>
              <CardContent className="p-0">
                {loadingIssues ? (
                  <div className="p-8 text-center text-slate-500">Loading tenant issues...</div>
                ) : issues.filter(i => i.status !== 'job_created' && i.status !== 'resolved' && i.status !== 'closed').length === 0 ? (
                  <div className="p-12 text-center text-slate-500">
                    <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                    <p className="font-medium">No open tenant issues</p>
                    <p className="text-sm mt-1">When tenants report issues, they'll appear here ready to convert into jobs.</p>
                  </div>
                ) : (
                  <div className="divide-y" data-testid="issues-inbox-list">
                    {issues
                      .filter(i => i.status !== 'job_created' && i.status !== 'resolved' && i.status !== 'closed')
                      .map(issue => (
                      <div 
                        key={issue.issue_id}
                        className="p-4 hover:bg-slate-50 flex items-start justify-between gap-4"
                        data-testid={`issue-row-${issue.issue_id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <p className="font-medium text-slate-900 capitalize">
                              {issue.issue_type?.replace(/_/g, ' ') || 'Issue'}
                            </p>
                            {issue.is_emergency && (
                              <Badge className="bg-red-100 text-red-800 gap-1">
                                <AlertTriangle className="h-3 w-3" />
                                Emergency
                              </Badge>
                            )}
                            {!issue.is_emergency && issue.severity === 'high' && (
                              <Badge className="bg-orange-100 text-orange-800">High</Badge>
                            )}
                            {issue.severity === 'medium' && !issue.is_emergency && (
                              <Badge className="bg-yellow-100 text-yellow-800">Medium</Badge>
                            )}
                            {issue.severity === 'low' && !issue.is_emergency && (
                              <Badge className="bg-slate-100 text-slate-700">Low</Badge>
                            )}
                            <Badge variant="outline" className="text-xs">{issue.status}</Badge>
                          </div>
                          <p className="text-sm text-slate-600 line-clamp-2 mb-2">{issue.description}</p>
                          <div className="flex items-center gap-4 text-xs text-slate-500 flex-wrap">
                            {issue.tenant_name && (
                              <span className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {issue.tenant_name}
                              </span>
                            )}
                            {(issue.property_address || issue.property_postcode) && (
                              <span className="flex items-center gap-1">
                                <Building2 className="h-3 w-3" />
                                {issue.property_address || issue.property_postcode}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {new Date(issue.created_at).toLocaleDateString('en-GB')}
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-col gap-2 items-end shrink-0">
                          <Button
                            size="sm"
                            onClick={() => handleQuickJobFromIssue(issue)}
                            data-testid={`quick-job-btn-${issue.issue_id}`}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Create Job
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/maintenance/issue/${issue.issue_id}`)}
                          >
                            View Details
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="quotes">
            <Card>
              <CardContent className="p-8 text-center text-slate-500">
                <FileText className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <p>Quotes management coming soon</p>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="contractors">
            <div className="space-y-4">
              {/* Contractors Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Select value={contractorFilter} onValueChange={setContractorFilter}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Filter by type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Contractors</SelectItem>
                      <SelectItem value="active">Active Only</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="maintenance">Maintenance</SelectItem>
                      <SelectItem value="plumber">Plumbers</SelectItem>
                      <SelectItem value="electrician">Electricians</SelectItem>
                      <SelectItem value="gas_engineer">Gas Engineers</SelectItem>
                      <SelectItem value="handyman">Handymen</SelectItem>
                      <SelectItem value="cleaner">Cleaners</SelectItem>
                      <SelectItem value="builder">Builders</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-muted-foreground">
                    {filteredContractors.length} contractor{filteredContractors.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <Button onClick={() => {
                  setEditingContractor(null);
                  resetContractorForm();
                  setShowAddContractorModal(true);
                }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Contractor
                </Button>
              </div>
              
              {/* Trade-based filter chips */}
              {contractors.length > 0 && (() => {
                // Build per-trade counts from current contractors (covers both new
                // schema "categories" array and legacy "specialty" single value)
                const counts = {};
                contractors.forEach(c => {
                  const trades = (c.categories && c.categories.length > 0)
                    ? c.categories
                    : (c.specialty ? [c.specialty] : []);
                  trades.forEach(t => {
                    if (!t) return;
                    counts[t] = (counts[t] || 0) + 1;
                  });
                });
                const tradeChips = Object.entries(counts)
                  .map(([value, count]) => ({ value, label: tradeLabel(value), count }))
                  .filter(opt => opt.count > 0)
                  .sort((a, b) => b.count - a.count);
                
                if (tradeChips.length === 0) return null;
                
                return (
                  <div className="flex items-center gap-2 flex-wrap" data-testid="trade-filter-chips">
                    <button
                      type="button"
                      onClick={() => setContractorFilter('all')}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                        contractorFilter === 'all'
                          ? 'bg-slate-900 text-white border-slate-900'
                          : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400 hover:bg-slate-50'
                      }`}
                      data-testid="trade-chip-all"
                    >
                      All
                      <span className={`ml-1.5 inline-flex items-center justify-center rounded-full px-1.5 text-xs ${
                        contractorFilter === 'all' ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {contractors.length}
                      </span>
                    </button>
                    {tradeChips.map(opt => {
                      const isActive = contractorFilter === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setContractorFilter(isActive ? 'all' : opt.value)}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                            isActive
                              ? 'bg-emerald-600 text-white border-emerald-600'
                              : 'bg-white text-slate-700 border-slate-200 hover:border-emerald-400 hover:bg-emerald-50'
                          }`}
                          data-testid={`trade-chip-${opt.value}`}
                        >
                          {opt.label}
                          <span className={`ml-1.5 inline-flex items-center justify-center rounded-full px-1.5 text-xs ${
                            isActive ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
                          }`}>
                            {opt.count}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
              
              {/* Contractors Grid */}
              {loadingContractors ? (
                <div className="p-8 text-center text-slate-500">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                  Loading contractors...
                </div>
              ) : filteredContractors.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center text-slate-500">
                    <HardHat className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                    <p className="font-medium">No contractors found</p>
                    <p className="text-sm mt-1">Add your first contractor to get started</p>
                    <Button className="mt-4" onClick={() => {
                      setEditingContractor(null);
                      resetContractorForm();
                      setShowAddContractorModal(true);
                    }}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Contractor
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredContractors.map(contractor => {
                    const insuranceStatus = getInsuranceStatus(contractor);
                    const companyDisplay = contractor.company_name || contractor.company;
                    const contactDisplay = contractor.contact_name || contractor.name;
                    return (
                      <Card
                        key={contractor.contractor_id}
                        className="hover:shadow-md hover:border-slate-300 transition-all cursor-pointer"
                        onClick={() => setViewingContractor(contractor)}
                        data-testid={`contractor-card-${contractor.contractor_id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                                <HardHat className="w-6 h-6 text-slate-600" />
                              </div>
                              <div className="min-w-0">
                                <p className="font-semibold text-slate-900 truncate" title={companyDisplay || contactDisplay || 'Unnamed Contractor'}>
                                  {companyDisplay || contactDisplay || 'Unnamed Contractor'}
                                </p>
                                {companyDisplay && contactDisplay && (
                                  <p className="text-sm text-slate-500 flex items-center gap-1 truncate" title={contactDisplay}>
                                    <User className="w-3 h-3 shrink-0" />
                                    {contactDisplay}
                                  </p>
                                )}
                              </div>
                            </div>
                            <Badge className={
                              contractor.status === 'active' ? 'bg-green-100 text-green-700' :
                              contractor.status === 'suspended' ? 'bg-red-100 text-red-700' :
                              'bg-slate-100 text-slate-700'
                            }>
                              {contractor.status || 'pending'}
                            </Badge>
                          </div>
                          
                          {/* Contact Info */}
                          <div className="space-y-1 mb-3">
                            {contractor.phone && (
                              <p className="text-sm flex items-center gap-2 text-slate-600">
                                <Phone className="w-4 h-4 text-slate-400" />
                                <a href={`tel:${contractor.phone}`} className="hover:text-blue-600" onClick={(e) => e.stopPropagation()}>{contractor.phone}</a>
                              </p>
                            )}
                            {contractor.email && (
                              <p className="text-sm flex items-center gap-2 text-slate-600">
                                <Mail className="w-4 h-4 text-slate-400" />
                                <a href={`mailto:${contractor.email}`} className="hover:text-blue-600 truncate" onClick={(e) => e.stopPropagation()}>{contractor.email}</a>
                              </p>
                            )}
                            {(contractor.address_line1 || contractor.city || contractor.postcode) && (() => {
                              const short = [contractor.city, contractor.postcode].filter(Boolean).join(', ');
                              const full = [contractor.address_line1, contractor.address_line2, contractor.city, contractor.postcode]
                                .filter(Boolean).join(', ');
                              return (
                                <p className="text-sm flex items-center gap-2 text-slate-600 truncate" title={full}>
                                  <MapPin className="w-4 h-4 text-slate-400 shrink-0" />
                                  <span className="truncate">{short || contractor.address_line1}</span>
                                </p>
                              );
                            })()}
                          </div>
                          
                          {/* Categories/Services */}
                          {contractor.categories?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-3">
                              {contractor.categories.slice(0, 4).map(cat => (
                                <Badge key={cat} variant="outline" className="text-xs">
                                  {tradeLabel(cat)}
                                </Badge>
                              ))}
                              {contractor.categories.length > 4 && (
                                <Badge variant="outline" className="text-xs">
                                  +{contractor.categories.length - 4}
                                </Badge>
                              )}
                            </div>
                          )}
                          
                          {/* Insurance & Rate */}
                          <div className="flex items-center justify-between py-2 border-t text-sm">
                            <div className="flex items-center gap-1">
                              <Shield className={`w-4 h-4 ${
                                insuranceStatus === 'valid' ? 'text-green-500' :
                                insuranceStatus === 'expiring' ? 'text-amber-500' :
                                insuranceStatus === 'expired' ? 'text-red-500' :
                                'text-slate-300'
                              }`} />
                              <span className={
                                insuranceStatus === 'valid' ? 'text-green-600' :
                                insuranceStatus === 'expiring' ? 'text-amber-600' :
                                insuranceStatus === 'expired' ? 'text-red-600' :
                                'text-slate-400'
                              }>
                                {insuranceStatus === 'valid' ? 'Insured' :
                                 insuranceStatus === 'expiring' ? 'Expiring Soon' :
                                 insuranceStatus === 'expired' ? 'Expired' :
                                 'Not verified'}
                              </span>
                            </div>
                            {contractor.hourly_rate && (
                              <span className="font-medium text-emerald-600">
                                £{contractor.hourly_rate}/hr
                              </span>
                            )}
                          </div>
                          
                          {/* Actions */}
                          <div className="flex items-center gap-2 pt-2 border-t mt-2" onClick={(e) => e.stopPropagation()}>
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1"
                              onClick={() => openEditContractor(contractor)}
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                            {contractor.email && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleSendInvite(contractor)}
                                title={contractor.portal_invited ? 'Resend portal access' : 'Send portal invite'}
                                data-testid={`invite-btn-${contractor.contractor_id}`}
                              >
                                {contractor.portal_invited ? <RefreshCw className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500 hover:text-red-600 hover:bg-red-50"
                              onClick={() => handleDeleteContractor(contractor.contractor_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>
          
          <TabsContent value="invoices">
            <Card>
              <CardContent className="p-8 text-center text-slate-500">
                <Receipt className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <p>View invoices in the Accounts section</p>
                <Button variant="outline" className="mt-4" onClick={() => navigate('/accounts/invoicing')}>
                  Go to Invoicing
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Add/Edit Contractor Modal */}
      <Dialog open={showAddContractorModal} onOpenChange={setShowAddContractorModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HardHat className="w-5 h-5 text-amber-600" />
              {editingContractor ? 'Edit Contractor' : 'Add New Contractor'}
            </DialogTitle>
            <DialogDescription>
              {editingContractor 
                ? 'Update contractor details and services'
                : 'Add a new contractor to your portfolio'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contact Name *</Label>
                <Input
                  value={contractorForm.contact_name}
                  onChange={(e) => setContractorForm({ ...contractorForm, contact_name: e.target.value })}
                  placeholder="John Smith"
                />
              </div>
              <div>
                <Label>Company Name</Label>
                <Input
                  value={contractorForm.company_name}
                  onChange={(e) => setContractorForm({ ...contractorForm, company_name: e.target.value })}
                  placeholder="Smith Plumbing Ltd"
                />
              </div>
            </div>
            
            {/* Contact Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Phone *</Label>
                <Input
                  value={contractorForm.phone}
                  onChange={(e) => setContractorForm({ ...contractorForm, phone: e.target.value })}
                  placeholder="07123 456789"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={contractorForm.email}
                  onChange={(e) => setContractorForm({ ...contractorForm, email: e.target.value })}
                  placeholder="john@smithplumbing.com"
                />
              </div>
            </div>
            
            {/* Address */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold text-slate-700">Address</Label>
              <Input
                value={contractorForm.address_line1}
                onChange={(e) => setContractorForm({ ...contractorForm, address_line1: e.target.value })}
                placeholder="Address line 1"
                data-testid="contractor-address-line1"
              />
              <Input
                value={contractorForm.address_line2}
                onChange={(e) => setContractorForm({ ...contractorForm, address_line2: e.target.value })}
                placeholder="Address line 2 (optional)"
                data-testid="contractor-address-line2"
              />
              <div className="grid grid-cols-2 gap-4">
                <Input
                  value={contractorForm.city}
                  onChange={(e) => setContractorForm({ ...contractorForm, city: e.target.value })}
                  placeholder="City / Town"
                  data-testid="contractor-city"
                />
                <Input
                  value={contractorForm.postcode}
                  onChange={(e) => setContractorForm({ ...contractorForm, postcode: e.target.value.toUpperCase() })}
                  placeholder="Postcode"
                  className="uppercase"
                  data-testid="contractor-postcode"
                />
              </div>
            </div>
            
            {/* Categories/Trades */}
            <div>
              <Label className="mb-2 block">Trades / Categories</Label>
              <div className="flex flex-wrap gap-2">
                {categoryOptions.map(cat => (
                  <Badge
                    key={cat.value}
                    variant={contractorForm.categories.includes(cat.value) ? "default" : "outline"}
                    className={`cursor-pointer ${
                      contractorForm.categories.includes(cat.value) 
                        ? 'bg-amber-600 hover:bg-amber-700' 
                        : 'hover:bg-slate-100'
                    }`}
                    onClick={() => toggleCategory(cat.value)}
                  >
                    {cat.label}
                  </Badge>
                ))}
                {/* Custom (non-standard) trades — selected/removable inline */}
                {contractorForm.categories
                  .filter(v => !categoryOptions.some(o => o.value === v))
                  .map(v => (
                    <Badge
                      key={v}
                      className="bg-emerald-600 hover:bg-emerald-700 cursor-pointer gap-1"
                      onClick={() => toggleCategory(v)}
                      data-testid={`custom-trade-${v}`}
                      title="Click to remove"
                    >
                      {tradeLabel(v)}
                      <XCircle className="w-3 h-3 opacity-80" />
                    </Badge>
                  ))}
              </div>
              
              {/* Add custom trade */}
              <div className="flex items-center gap-2 mt-3">
                <Input
                  value={customTradeInput}
                  onChange={(e) => setCustomTradeInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addCustomTrade();
                    }
                  }}
                  placeholder="Add another trade (e.g. Scaffolder, Tree Surgeon)"
                  className="text-sm"
                  data-testid="custom-trade-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addCustomTrade}
                  data-testid="custom-trade-add-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Type a trade not in the list above and press Enter (or click Add). Custom trades appear as green chips.
              </p>
            </div>
            
            {/* Rate & Insurance */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Hourly Rate (£)</Label>
                <Input
                  type="number"
                  value={contractorForm.hourly_rate}
                  onChange={(e) => setContractorForm({ ...contractorForm, hourly_rate: e.target.value })}
                  placeholder="45.00"
                />
              </div>
              <div>
                <Label>Insurance Expiry Date</Label>
                <Input
                  type="date"
                  value={contractorForm.insurance_expiry}
                  onChange={(e) => setContractorForm({ ...contractorForm, insurance_expiry: e.target.value })}
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="insurance-verified"
                checked={contractorForm.insurance_verified}
                onChange={(e) => setContractorForm({ ...contractorForm, insurance_verified: e.target.checked })}
                className="rounded border-gray-300"
              />
              <Label htmlFor="insurance-verified" className="flex items-center gap-1 cursor-pointer">
                <Shield className="w-4 h-4 text-green-500" />
                Insurance verified and on file
              </Label>
            </div>
            
            {/* Notes */}
            <div>
              <Label>Notes</Label>
              <Textarea
                value={contractorForm.notes}
                onChange={(e) => setContractorForm({ ...contractorForm, notes: e.target.value })}
                placeholder="Any additional notes about this contractor..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddContractorModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveContractor}
              disabled={savingContractor || !contractorForm.contact_name || !contractorForm.phone}
            >
              {savingContractor ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              {editingContractor ? 'Update Contractor' : 'Add Contractor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Job Modal */}
      <Dialog open={showNewJobModal} onOpenChange={setShowNewJobModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-blue-600" />
              {jobForm.issue_id ? 'Create Job from Tenant Issue' : 'Create New Maintenance Job'}
            </DialogTitle>
            <DialogDescription>
              {jobForm.issue_id
                ? 'Details have been pre-filled from the tenant\'s reported issue. Review, adjust if needed, and create the job.'
                : 'Log a new maintenance job for a property'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Property Selection */}
            <div>
              <Label>Property</Label>
              <Select
                value={jobForm.property_id}
                onValueChange={(value) => {
                  const prop = properties.find(p => p.property_id === value);
                  setJobForm({ 
                    ...jobForm, 
                    property_id: value,
                    property_address: prop?.address || ''
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a property (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {properties.slice(0, 100).map(prop => (
                    <SelectItem key={prop.property_id} value={prop.property_id}>
                      {prop.address}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {!jobForm.property_id && (
              <div>
                <Label>Or enter address manually</Label>
                <Input
                  value={jobForm.property_address}
                  onChange={(e) => setJobForm({ ...jobForm, property_address: e.target.value })}
                  placeholder="123 Main Street, London"
                />
              </div>
            )}
            
            {/* Issue Type & Priority */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Issue Type *</Label>
                <Select
                  value={jobForm.issue_type}
                  onValueChange={(value) => setJobForm({ ...jobForm, issue_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select issue type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="plumbing">Plumbing</SelectItem>
                    <SelectItem value="electrical">Electrical</SelectItem>
                    <SelectItem value="heating">Heating / Boiler</SelectItem>
                    <SelectItem value="gas">Gas</SelectItem>
                    <SelectItem value="appliance">Appliance Repair</SelectItem>
                    <SelectItem value="lock">Lock / Keys</SelectItem>
                    <SelectItem value="pest">Pest Control</SelectItem>
                    <SelectItem value="damp">Damp / Mould</SelectItem>
                    <SelectItem value="roof">Roofing</SelectItem>
                    <SelectItem value="window">Windows / Doors</SelectItem>
                    <SelectItem value="garden">Garden / External</SelectItem>
                    <SelectItem value="cleaning">Cleaning</SelectItem>
                    <SelectItem value="general">General Repair</SelectItem>
                    <SelectItem value="inspection">Property Inspection</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select
                  value={jobForm.priority}
                  onValueChange={(value) => setJobForm({ ...jobForm, priority: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="emergency">🔴 Emergency</SelectItem>
                    <SelectItem value="high">🟠 High</SelectItem>
                    <SelectItem value="medium">🟡 Medium</SelectItem>
                    <SelectItem value="low">🟢 Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Description */}
            <div>
              <Label>Description *</Label>
              <Textarea
                value={jobForm.description}
                onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })}
                placeholder="Describe the issue in detail..."
                rows={3}
              />
            </div>
            
            {/* Assign Contractor & Schedule */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                {(() => {
                  // Map issue_type to relevant trade categories used in contractor profiles
                  const issueTypeToTrades = {
                    plumbing: ['plumber', 'gas_engineer', 'maintenance'],
                    electrical: ['electrician', 'maintenance'],
                    heating: ['gas_engineer', 'plumber', 'maintenance'],
                    gas: ['gas_engineer'],
                    appliance: ['appliance_repair', 'electrician', 'handyman', 'maintenance'],
                    lock: ['locksmith', 'handyman', 'maintenance'],
                    pest: ['pest_control'],
                    damp: ['builder', 'painter', 'handyman', 'maintenance'],
                    roof: ['roofer', 'builder', 'maintenance'],
                    window: ['handyman', 'builder', 'maintenance'],
                    garden: ['gardener', 'handyman', 'maintenance'],
                    cleaning: ['cleaner'],
                    general: ['handyman', 'builder', 'maintenance'],
                    inspection: ['inventory_clerk', 'handyman', 'maintenance'],
                    other: ['maintenance']
                  };
                  const relevantTrades = issueTypeToTrades[jobForm.issue_type] || [];
                  const activeContractors = contractors.filter(c => c.status === 'active');
                  
                  // Smart filter: when an issue type is selected, only show contractors
                  // whose categories/specialty match relevant trades AND whose insurance
                  // is not expired. User can opt out via "Show all contractors" toggle.
                  let listed = activeContractors;
                  let smartFilterApplied = false;
                  if (!showAllContractorsInJobForm && relevantTrades.length > 0) {
                    const matched = activeContractors.filter(c => {
                      const trades = (c.categories && c.categories.length > 0)
                        ? c.categories
                        : (c.specialty ? [c.specialty] : []);
                      const hasTrade = trades.some(t => relevantTrades.includes(t));
                      const insuranceStatus = getInsuranceStatus(c);
                      return hasTrade && insuranceStatus !== 'expired';
                    });
                    if (matched.length > 0) {
                      listed = matched;
                      smartFilterApplied = true;
                    }
                  }
                  
                  const totalActive = activeContractors.length;
                  
                  return (
                    <>
                      <div className="flex items-center justify-between mb-1">
                        <Label>Assign Contractor (optional)</Label>
                        {smartFilterApplied && (
                          <button
                            type="button"
                            onClick={() => setShowAllContractorsInJobForm(true)}
                            className="text-xs text-blue-600 hover:underline"
                            data-testid="show-all-contractors-link"
                          >
                            Show all ({totalActive})
                          </button>
                        )}
                        {!smartFilterApplied && showAllContractorsInJobForm && relevantTrades.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setShowAllContractorsInJobForm(false)}
                            className="text-xs text-blue-600 hover:underline"
                            data-testid="match-trade-link"
                          >
                            Match trade only
                          </button>
                        )}
                      </div>
                      <Select
                        value={jobForm.contractor_id}
                        onValueChange={(value) => setJobForm({ ...jobForm, contractor_id: value })}
                      >
                        <SelectTrigger data-testid="assign-contractor-trigger">
                          <SelectValue placeholder={
                            listed.length === 0
                              ? (smartFilterApplied ? 'No matching contractors' : 'Select contractor')
                              : 'Select contractor'
                          } />
                        </SelectTrigger>
                        <SelectContent>
                          {listed.length === 0 ? (
                            <div className="px-2 py-3 text-sm text-slate-500">
                              No active contractors{relevantTrades.length > 0 ? ' for this trade' : ''}.
                            </div>
                          ) : (
                            listed.map(c => {
                              const insStatus = getInsuranceStatus(c);
                              const company = c.company_name || c.company;
                              const contact = c.contact_name || c.name || 'Contractor';
                              return (
                                <SelectItem key={c.contractor_id} value={c.contractor_id}>
                                  <span className="flex items-center gap-2">
                                    <span>{company || contact}{company && contact ? ` — ${contact}` : ''}</span>
                                    {insStatus === 'expiring' && (
                                      <span className="text-[10px] text-amber-600 font-medium">⚠ insurance expiring</span>
                                    )}
                                    {insStatus === 'expired' && (
                                      <span className="text-[10px] text-red-600 font-medium">⚠ insurance expired</span>
                                    )}
                                  </span>
                                </SelectItem>
                              );
                            })
                          )}
                        </SelectContent>
                      </Select>
                      {smartFilterApplied && (
                        <p className="text-xs text-slate-500 mt-1" data-testid="smart-filter-hint">
                          Showing {listed.length} matching {listed.length === 1 ? 'contractor' : 'contractors'} with valid insurance
                        </p>
                      )}
                    </>
                  );
                })()}
              </div>
              <div>
                <Label>Scheduled Date</Label>
                <Input
                  type="date"
                  value={jobForm.scheduled_date}
                  onChange={(e) => setJobForm({ ...jobForm, scheduled_date: e.target.value })}
                />
              </div>
            </div>
            
            {/* Access Instructions */}
            <div>
              <Label>Access Instructions</Label>
              <Input
                value={jobForm.access_instructions}
                onChange={(e) => setJobForm({ ...jobForm, access_instructions: e.target.value })}
                placeholder="e.g., Key in lockbox, code 1234"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowNewJobModal(false); setShowAllContractorsInJobForm(false); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateJob}
              disabled={savingJob || !jobForm.issue_type || !jobForm.description}
            >
              {savingJob ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Create Job
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contractor Details View Modal */}
      <Dialog open={!!viewingContractor} onOpenChange={(open) => !open && setViewingContractor(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="contractor-detail-modal">
          {viewingContractor && (() => {
            const c = viewingContractor;
            const companyDisplay = c.company_name || c.company;
            const contactDisplay = c.contact_name || c.name;
            const insuranceStatus = getInsuranceStatus(c);
            return (
              <>
                <DialogHeader>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center shrink-0">
                        <HardHat className="w-7 h-7 text-amber-600" />
                      </div>
                      <div className="min-w-0">
                        <DialogTitle className="text-xl truncate" title={companyDisplay || contactDisplay || 'Unnamed Contractor'}>
                          {companyDisplay || contactDisplay || 'Unnamed Contractor'}
                        </DialogTitle>
                        {companyDisplay && contactDisplay && (
                          <DialogDescription className="flex items-center gap-1.5 mt-1">
                            <User className="w-3.5 h-3.5" />
                            {contactDisplay}
                          </DialogDescription>
                        )}
                      </div>
                    </div>
                    <Badge className={
                      c.status === 'active' ? 'bg-green-100 text-green-700' :
                      c.status === 'suspended' ? 'bg-red-100 text-red-700' :
                      'bg-slate-100 text-slate-700'
                    }>
                      {c.status || 'pending'}
                    </Badge>
                  </div>
                </DialogHeader>

                <div className="space-y-5 py-2">
                  {/* Contact */}
                  <section>
                    <h3 className="text-xs font-semibold uppercase text-slate-500 tracking-wide mb-2">Contact</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-slate-400 shrink-0" />
                        {c.phone ? (
                          <a href={`tel:${c.phone}`} className="text-blue-600 hover:underline">{c.phone}</a>
                        ) : (
                          <span className="text-slate-400">No phone</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 min-w-0">
                        <Mail className="w-4 h-4 text-slate-400 shrink-0" />
                        {c.email ? (
                          <a href={`mailto:${c.email}`} className="text-blue-600 hover:underline truncate" title={c.email}>{c.email}</a>
                        ) : (
                          <span className="text-slate-400">No email</span>
                        )}
                      </div>
                    </div>
                  </section>

                  {/* Address */}
                  {(c.address_line1 || c.address_line2 || c.city || c.postcode) && (() => {
                    const lines = [c.address_line1, c.address_line2, c.city, c.postcode].filter(Boolean);
                    const mapQuery = encodeURIComponent(lines.join(', '));
                    return (
                      <section>
                        <h3 className="text-xs font-semibold uppercase text-slate-500 tracking-wide mb-2">Address</h3>
                        <div className="flex items-start gap-2 text-sm">
                          <MapPin className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            {lines.map((line, i) => (
                              <p key={i} className="text-slate-700">{line}</p>
                            ))}
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${mapQuery}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:underline mt-1 inline-block"
                            >
                              Open in Google Maps →
                            </a>
                          </div>
                        </div>
                      </section>
                    );
                  })()}

                  {/* Trades & Services */}
                  {(c.categories?.length > 0 || c.services?.length > 0) && (
                    <section>
                      <h3 className="text-xs font-semibold uppercase text-slate-500 tracking-wide mb-2">Trades & Services</h3>
                      {c.categories?.length > 0 && (
                        <div className="mb-2">
                          <p className="text-xs text-slate-500 mb-1">Categories</p>
                          <div className="flex flex-wrap gap-1.5">
                            {c.categories.map(cat => (
                              <Badge key={cat} variant="outline" className="text-xs">
                                {tradeLabel(cat)}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {c.services?.length > 0 && (
                        <div>
                          <p className="text-xs text-slate-500 mb-1">Services</p>
                          <div className="flex flex-wrap gap-1.5">
                            {c.services.map(s => (
                              <Badge key={s} variant="secondary" className="text-xs capitalize">
                                {s.replace(/_/g, ' ')}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </section>
                  )}

                  {/* Insurance & Rate */}
                  <section>
                    <h3 className="text-xs font-semibold uppercase text-slate-500 tracking-wide mb-2">Compliance & Pricing</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                      <div className="rounded-lg bg-slate-50 p-3">
                        <p className="text-xs text-slate-500 mb-1">Insurance</p>
                        <div className="flex items-center gap-1.5">
                          <Shield className={`w-4 h-4 ${
                            insuranceStatus === 'valid' ? 'text-green-500' :
                            insuranceStatus === 'expiring' ? 'text-amber-500' :
                            insuranceStatus === 'expired' ? 'text-red-500' :
                            'text-slate-300'
                          }`} />
                          <span className="font-medium capitalize">
                            {insuranceStatus === 'valid' ? 'Valid' :
                             insuranceStatus === 'expiring' ? 'Expiring Soon' :
                             insuranceStatus === 'expired' ? 'Expired' :
                             'Not verified'}
                          </span>
                        </div>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-3">
                        <p className="text-xs text-slate-500 mb-1">Expiry Date</p>
                        <p className="font-medium">
                          {c.insurance_expiry
                            ? new Date(c.insurance_expiry).toLocaleDateString('en-GB')
                            : <span className="text-slate-400 font-normal">Not set</span>}
                        </p>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-3">
                        <p className="text-xs text-slate-500 mb-1">Hourly Rate</p>
                        <p className="font-medium text-emerald-600">
                          {c.hourly_rate ? `£${c.hourly_rate}/hr` : <span className="text-slate-400 font-normal">Not set</span>}
                        </p>
                      </div>
                    </div>
                  </section>

                  {/* Notes */}
                  {c.notes && (
                    <section>
                      <h3 className="text-xs font-semibold uppercase text-slate-500 tracking-wide mb-2">Notes</h3>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 p-3 rounded-lg">{c.notes}</p>
                    </section>
                  )}

                  {/* Meta */}
                  <section className="text-xs text-slate-400 pt-2 border-t flex flex-wrap gap-x-4 gap-y-1">
                    <span>ID: {c.contractor_id}</span>
                    {c.created_at && <span>Added {new Date(c.created_at).toLocaleDateString('en-GB')}</span>}
                    {c.updated_at && <span>Updated {new Date(c.updated_at).toLocaleDateString('en-GB')}</span>}
                    {c.portal_invited && <span className="text-emerald-600">Portal access granted</span>}
                  </section>
                </div>

                <DialogFooter className="gap-2 sm:gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setViewingContractor(null)}
                    data-testid="contractor-detail-close"
                  >
                    Close
                  </Button>
                  {c.email && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        handleSendInvite(c);
                        setViewingContractor(null);
                      }}
                      data-testid="contractor-detail-resend"
                    >
                      {c.portal_invited ? <RefreshCw className="w-4 h-4 mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                      {c.portal_invited ? 'Resend Portal Access' : 'Send Portal Invite'}
                    </Button>
                  )}
                  <Button
                    onClick={() => {
                      setViewingContractor(null);
                      openEditContractor(c);
                    }}
                    data-testid="contractor-detail-edit"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit Contractor
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Portal Invite Result — shows the freshly generated temp password
          so an admin can copy/share it (outbound emails are disabled). */}
      <Dialog open={!!inviteResult} onOpenChange={(open) => !open && setInviteResult(null)}>
        <DialogContent className="max-w-md" data-testid="invite-result-modal">
          {inviteResult && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {inviteResult.resent ? (
                    <RefreshCw className="w-5 h-5 text-blue-600" />
                  ) : (
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  )}
                  Portal Access {inviteResult.resent ? 'Resent' : 'Created'}
                </DialogTitle>
                <DialogDescription>
                  Share the login details below with{' '}
                  <span className="font-medium text-slate-800">
                    {inviteResult.contractor?.contact_name || inviteResult.contractor?.name || 'the contractor'}
                  </span>
                  . Outbound email is currently disabled, so the new password is shown here for you to copy.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-3 py-2">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Email</p>
                  <div className="flex items-center justify-between gap-2">
                    <code className="text-sm font-medium text-slate-900 break-all">{inviteResult.email}</code>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="shrink-0"
                      onClick={() => {
                        navigator.clipboard?.writeText(inviteResult.email || '');
                        toast.success('Email copied');
                      }}
                      data-testid="invite-copy-email"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                
                {inviteResult.portal_url && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                    <p className="text-xs uppercase tracking-wide text-blue-700 mb-2">Portal Login Link</p>
                    <div className="flex items-start gap-3">
                      {/* QR code so contractor can scan from their phone */}
                      <div className="shrink-0 rounded-md bg-white p-1.5 border border-blue-200" data-testid="invite-portal-qr">
                        <QRCodeSVG value={inviteResult.portal_url} size={84} level="M" includeMargin={false} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <a
                            href={inviteResult.portal_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-medium text-blue-700 hover:text-blue-900 underline break-all flex-1"
                            data-testid="invite-portal-url"
                          >
                            {inviteResult.portal_url}
                          </a>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="shrink-0 -mt-1"
                            onClick={() => {
                              navigator.clipboard?.writeText(inviteResult.portal_url || '');
                              toast.success('Link copied');
                            }}
                            data-testid="invite-copy-portal-url"
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                        <p className="text-[11px] text-blue-700/80 mt-2 leading-snug">
                          Scan the QR with a phone camera to open the login page instantly.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {inviteResult.temp_password ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <p className="text-xs uppercase tracking-wide text-amber-700 mb-1">Temporary Password</p>
                    <div className="flex items-center justify-between gap-2">
                      <code className="text-sm font-bold text-slate-900 break-all" data-testid="invite-temp-password">
                        {inviteResult.temp_password}
                      </code>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="shrink-0"
                        onClick={() => {
                          navigator.clipboard?.writeText(inviteResult.temp_password || '');
                          toast.success('Password copied');
                        }}
                        data-testid="invite-copy-password"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                    <p className="text-xs text-amber-700 mt-2">
                      The contractor should change this password on first login.
                    </p>
                  </div>
                ) : (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-testid="invite-no-password-warning">
                    <p className="text-xs uppercase tracking-wide text-red-700 mb-1">No password returned</p>
                    <p className="text-sm text-red-800">
                      The server didn't include a temporary password. This usually means today's update hasn't been deployed yet (production needs to be redeployed). Click <strong>Resend</strong> after deploying to generate a new password.
                    </p>
                  </div>
                )}
                
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    const lines = ['Hi,', '', 'Your Goodacres Contractor Portal access:'];
                    lines.push(`Email: ${inviteResult.email}`);
                    if (inviteResult.temp_password) lines.push(`Temporary password: ${inviteResult.temp_password}`);
                    if (inviteResult.portal_url) lines.push(`Log in here: ${inviteResult.portal_url}`);
                    lines.push('', 'Please change your password on first login.', '', 'Goodacres Team');
                    navigator.clipboard?.writeText(lines.join('\n'));
                    toast.success('Login details copied to clipboard');
                  }}
                  data-testid="invite-copy-both"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy all as message
                </Button>
              </div>
              
              <DialogFooter>
                <Button onClick={() => setInviteResult(null)} data-testid="invite-result-close">
                  Done
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
